import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { QuickApplyApiService } from "../quick-apply-api.service";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { TalentHappyPathAuthService } from "../../shared/service/talent-happy-path-auth.service";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Hours, PersonType, PlacementType, PayTransparencyCategory, RateType } from "../../../enums";
import { TranslateService } from "@ngx-translate/core";
import { TalentHappyPathAuthApiService } from "../../shared/service/talent-happy-path-auth-api.service";
import { FeatureService, TalentAppFeatures } from '../../../service/feature.service';
import { QuickApplyWorkflowService } from "../quick-apply-workflow.service";

@Component({
  selector: 'app-quick-apply-invite',
  standalone: false,
  templateUrl: './quick-apply-invite.component.html',
  styleUrl: './quick-apply-invite.component.scss'
})
export class QuickApplyInviteComponent implements OnInit {
  emailForm: FormGroup;
  showJobDescription: boolean = false;
  posting: PostingInfo;
  postingExtraInfo: JobPostingExtraInfo;
  placementType: String;
  hoursType: String;
  payTransparencyFeature: boolean = false;
  minPay: number;
  maxPay: number;
  localeString: string = 'en-US'; // Default locale
  currencyCode: string = 'USD';   // Default currency

  placementTypeKeyMap: Record<PlacementType, string> = {
    [PlacementType.TEMP_TO_PERM]: 'temp-to-perm',
    [PlacementType.PERMANENT]: 'perm',
    [PlacementType.TEMPORARY]: 'temp',
    [PlacementType.PROJECT]: 'project'
  };

  hoursKeyMap: Record<Hours, string> = {
    [Hours.PART_TIME]: 'part-time',
    [Hours.FULL_TIME]: 'full-time'
  };

  constructor(private router: Router,
              private route: ActivatedRoute,
              private quickApplyService: QuickApplyApiService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private translate: TranslateService,
              private authService: TalentHappyPathAuthService,
              private authAPIService: TalentHappyPathAuthApiService,
              private featureService: FeatureService,
              private workflowService: QuickApplyWorkflowService) {
  }

  ngOnInit(): void {
    this.posting = this.talentHappyPathDataService.postingInfo;
    this.payTransparencyFeature = this.featureService.getFeature(TalentAppFeatures.APPLICANT_PAY_TRANSPARENCY_FEATURE);
    if (this.posting?.jobPostingId) {
      this.quickApplyService.readPostingExtraInfo(this.posting.jobPostingId).subscribe(data => {
        this.postingExtraInfo = data;
        this.getTranslatedJobPosting(data);

        // Set locale and currency based on posting info
        if (data.languageCode && data.countryCode) {
          this.localeString = `${data.languageCode}-${data.countryCode}`;
          this.currencyCode = this.getCurrencyCodeForCountry(data.countryCode);
        }

        // Set min and max pay
        this.minPay = data.minPay;
        this.maxPay = data.maxPay;
      }, error => {
        console.error("Error fetching job posting extra info: ", error);
      });
    }
    this.emailForm = new FormGroup({
      email: new FormControl('', [Validators.required, this.emailUsernameValidator]),
    });
  }

  emailUsernameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (control.value.includes('@')) {
      return Validators.email(control);
    } else {
      return Validators.pattern(/^[a-zA-Z0-9_. -]*$/)(control);
    }
  }

  toggleShowJobDesc() {
    this.showJobDescription = !this.showJobDescription;
  }

  getTranslatedJobPosting(job: JobPostingExtraInfo) {
    let placementTypeTranslation: string | undefined;
    let hoursTranslation: string | undefined;

    if (job.placementTypeId && this.placementTypeKeyMap[job.placementTypeId]) {
      const key = this.placementTypeKeyMap[job.placementTypeId];
      placementTypeTranslation = this.translate.instant(`talent-happy-path.quick-apply.invite-page.placement-type.${key}`);
    }

    if (job.hourId && this.hoursKeyMap[job.hourId]) {
      const key = this.hoursKeyMap[job.hourId];
      hoursTranslation = this.translate.instant(`talent-happy-path.quick-apply.invite-page.hours.${key}`);
    }

    this.placementType = placementTypeTranslation;
    this.hoursType = hoursTranslation;
  }

  // Get the pay unit for the min/max pay data
  getPayUnitKey(): string {
    if (!this.postingExtraInfo) {
      return 'pay-unit.hour-abbreviated';
    }

    const { placementTypeId, payTransparencyCategoryId, rateTypeId } = this.postingExtraInfo;

    if (placementTypeId === PlacementType.TEMP_TO_PERM) {
      if (payTransparencyCategoryId === PayTransparencyCategory.HOURLY) {
        return 'pay-unit.hour-abbreviated';
      } else if (payTransparencyCategoryId === PayTransparencyCategory.DAILY) {
        return 'pay-unit.day-abbreviated';
      } else if (payTransparencyCategoryId === PayTransparencyCategory.SALARY) {
        return 'pay-unit.year-abbreviated';
      }
    } else if (placementTypeId === PlacementType.PERMANENT) {
      return 'pay-unit.year-abbreviated';
    } else if (placementTypeId === PlacementType.TEMPORARY) {
      if (rateTypeId === RateType.HOURLY) {
        return 'pay-unit.hour-abbreviated';
      } else if (rateTypeId === RateType.DAILY) {
        return 'pay-unit.day-abbreviated';
      }
    } else if (placementTypeId === PlacementType.PROJECT) {
      return 'pay-unit.project-abbreviated';
    }

    // Default fallback
    return 'pay-unit.hour-abbreviated';
  }

  // Helper method to determine currency code based on country
  getCurrencyCodeForCountry(countryCode: string): string {
    const currencyMap: Record<string, string> = {
      'US': 'USD',
      'CA': 'CAD',
      'GB': 'GBP',
      'FR': 'EUR',
      'DE': 'EUR',
      'JP': 'JPY',
      // Add more mappings as needed
    };
    return currencyMap[countryCode] || 'USD'; // Default to USD if not found
  }

  submit(): void {

    // if user is authenticated, load talent data from token
    this.talentHappyPathDataService.talent = null;

    if (this.authService.isAuthenticated()) {
      // Redirect authenticated users to the profile page
      this.authAPIService.searchTalentByToken(this.authService.authentication.id_token, String(this.talentHappyPathDataService.postingId))
        .subscribe(async data => {
          this.talentHappyPathDataService.talent = data;
          if (data.email === this.emailForm.value.email) {
            await this.workflowService.navigateToState('submit', {} , { relativeTo: this.route.parent });
          } else {
            await this.workflowService.navigateToState('otp', {}, { queryParams: { from: "invite" }, skipLocationChange: true, relativeTo: this.route.parent});
          }
        });
    } else {
      this.authAPIService.searchTalent(this.emailForm.value.email, this.talentHappyPathDataService.postingId).subscribe(async data => {
        if (data.personId === null
          || data.personId === undefined
          || (data.personTypeId === PersonType.EMPLOYEE
            || data.personTypeId === PersonType.PARTNER
            || data.personTypeId === PersonType.APPLICANT)) {
          this.talentHappyPathDataService.talent = data;
          await this.workflowService.navigateToState('otp', {}, { queryParams: { from: "invite" }, skipLocationChange: true, relativeTo: this.route.parent});
        }
      }, error => {
        this.emailForm.controls['email'].setErrors({invalid: true});
      });
    }
  }


}
